import React, { useEffect, useRef, useState } from 'react';
import I18n from 'modules/i18n';
import Icon from '@/components/ui/Icon';
import cn from '@/utils/cn';

const LEFT_NAV_ID = 'left_nav_bar';
const OPEN_BODY_CLASS = 'left-nav-open';

const HamburgerGlyph = () => (
  <svg
    aria-hidden
    fill="none"
    height="1.5rem"
    stroke="currentColor"
    strokeLinecap="round"
    strokeWidth="2"
    viewBox="0 0 24 24"
    width="1.5rem"
  >
    <line x1="4" x2="20" y1="7" y2="7" />
    <line x1="4" x2="20" y1="12" y2="12" />
    <line x1="4" x2="20" y1="17" y2="17" />
  </svg>
);

const LeftNavHamburger = () => {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  // AIDEV-NOTE: wasOpenRef gates focus restoration to a true open→close transition.
  // Without it, the `!open` branch would call buttonRef.current?.focus() on initial
  // mount and steal focus from wherever the user actually was on the page.
  const wasOpenRef = useRef(false);

  useEffect(() => {
    document.body.classList.toggle(OPEN_BODY_CLASS, open);

    if (!open) {
      if (wasOpenRef.current) buttonRef.current?.focus();
      wasOpenRef.current = false;
      return undefined;
    }

    wasOpenRef.current = true;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // A menu opened from the drawer portals to <body> and owns this Escape —
      // Radix closes it and restores focus; the drawer closes on the next press.
      const target = e.target as Element;
      if (target.closest?.('[data-radix-popper-content-wrapper]')) return;
      setOpen(false);
    };

    const onMouseDown = (e: MouseEvent) => {
      const nav = document.getElementById(LEFT_NAV_ID);
      const target = e.target as Element;
      if (nav?.contains(target)) return;
      if (buttonRef.current?.contains(target)) return;
      // Menus for controls inside the drawer (e.g. the sign-out dropdown) portal to
      // <body>, so their clicks register as outside the nav — don't dismiss for those.
      if (target.closest?.('[data-radix-popper-content-wrapper]')) return;
      setOpen(false);
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onMouseDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [open]);

  useEffect(() => () => {
    document.body.classList.remove(OPEN_BODY_CLASS);
  }, []);

  const label = open
    ? I18n.t('close_navigation_menu')
    : I18n.t('open_navigation_menu');

  // AIDEV-NOTE: `reflow-hamburger` intentionally mixes a non-Tailwind class with
  // utilities — visibility depends on (body.reflow-enabled AND viewport < 900px),
  // which can't be expressed with stock Tailwind variants (no max-width responsive
  // variant, no ancestor-class variant). The display toggle lives in
  // _reflow_shared.scss next to the drawer rules that share the same conditions.
  const classes = cn(
    'reflow-hamburger',
    'tw-box-border tw-items-center tw-justify-center tw-rounded',
    'tw-bg-transparent tw-p-2 tw-text-white tw-border-0 tw-cursor-pointer',
    'hover:tw-bg-white/10 focus:tw-outline focus:tw-outline-2 focus:tw-outline-white',
  );

  const handleToggle = () => setOpen(v => !v);

  return (
    <button
      ref={buttonRef}
      aria-controls={LEFT_NAV_ID}
      aria-expanded={open}
      aria-label={label}
      className={classes}
      type="button"
      onClick={handleToggle}
    >
      {open ? <Icon icon="Close" size="1.5rem" /> : <HamburgerGlyph />}
    </button>
  );
};

export default LeftNavHamburger;
